<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container link-clicks-page">
        <section class="analytics-hero">
          <div class="analytics-hero__copy">
            <p class="eyebrow">Yamamoto x Fusco Fit</p>
            <h1>Link Click Analytics</h1>
            <p class="subtitle">
              Dashboard operativa per leggere click, campagne e qualità del traffico con un colpo d'occhio.
            </p>
            <div class="hero-meta">
              <span class="status-chip" :class="hasMore ? 'status-chip--warn' : 'status-chip--ok'">
                {{ hasMore ? 'Dataset parziale' : 'Dataset completo' }}
              </span>
              <span class="hero-meta__text">{{ loadedInfo }}</span>
            </div>
          </div>

          <div class="hero-highlight">
            <span class="hero-highlight__label">Click filtrati</span>
            <strong class="hero-highlight__value">{{ formatNumber(filteredRows.length) }}</strong>
            <span class="hero-highlight__meta">{{ filterSummaryText }}</span>
          </div>
        </section>

        <section class="card-surface accordion">
          <button
            type="button"
            class="accordion__summary"
            :aria-expanded="shareLinksOpen ? 'true' : 'false'"
            @click="shareLinksOpen = !shareLinksOpen"
          >
            <div class="accordion__heading accordion__heading--left">
              <h2>Link di condivisione</h2>
              <p>Tracking attivo e shortlink pubblico consigliato per WhatsApp.</p>
            </div>
            <div class="accordion__meta">
              <span class="section-note">
                Il click viene registrato sempre su <strong>linkClicks</strong>.
              </span>
              <span class="material-icons accordion__icon" :class="{ 'accordion__icon--open': shareLinksOpen }">expand_more</span>
            </div>
          </button>

          <div v-show="shareLinksOpen" class="accordion__content">
            <div v-if="!hasTrackingConfig" class="info-banner info-banner--warn">
              Configurazione tracking non trovata. Il fallback rimane il link diretto Yamamoto senza analytics.
            </div>

            <div class="share-grid">
              <article v-for="entry in trackedLinks" :key="entry.key" class="share-card">
                <div class="share-card__header">
                  <div>
                    <h3>{{ entry.label }}</h3>
                    <p>{{ entry.description }}</p>
                  </div>
                  <span class="tag" :class="entry.maskedUrl ? 'tag--accent' : 'tag--neutral'">
                    {{ entry.maskedUrl ? 'Shortlink previsto' : 'Tracking live' }}
                  </span>
                </div>

                <div class="link-stack">
                  <div class="link-block">
                    <span class="link-block__label">Endpoint tracking attivo</span>
                    <p class="link-block__value" :title="entry.url">{{ entry.url }}</p>
                    <div class="copy-actions">
                      <button type="button" class="btn primary staff-button-neutral" @click="copyToClipboard(entry.url, entry.label)">
                        Copia tracking
                      </button>
                      <a class="btn ghost" :href="entry.url" target="_blank" rel="noopener">Apri</a>
                    </div>
                  </div>

                  <div v-if="entry.maskedUrl" class="link-block link-block--secondary">
                    <span class="link-block__label">URL pubblico consigliato</span>
                    <p class="link-block__value" :title="entry.maskedUrl">{{ entry.maskedUrl }}</p>
                    <p class="link-block__hint">{{ entry.maskedHint }}</p>
                    <div class="copy-actions">
                      <button type="button" class="btn ghost" @click="copyToClipboard(entry.maskedUrl, `${entry.label} shortlink`)">
                        Copia shortlink
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div class="shortlink-note">
              <strong>Implementazione consigliata.</strong>
              Il path pubblico <strong>/whatsapp/yamamoto</strong> deve fare redirect <strong>302</strong> verso
              <span class="mono-inline">{{ whatsappRedirectTarget }}</span>.
            </div>

            <p v-if="copyToast" class="copy-toast">{{ copyToast }}</p>
          </div>
        </section>

        <section class="card-surface filters-card">
          <div class="section-heading filters-card__heading">
            <div>
              <h2>Filtri e azioni</h2>
              <p>Filtra il dataset caricato, ricarica da Firestore e scarica il CSV del report corrente.</p>
            </div>
          </div>

          <div class="filters-grid">
            <label class="field-group">
              <span>Da</span>
              <input v-model="fromDate" type="date" />
            </label>

            <label class="field-group">
              <span>A</span>
              <input v-model="toDate" type="date" />
            </label>

            <label class="field-group">
              <span>Source</span>
              <select v-model="sourceFilter">
                <option value="">Tutte</option>
                <option v-for="option in sourceOptions" :key="option" :value="option">
                  {{ formatSourceLabel(option) }}
                </option>
              </select>
            </label>

            <label class="field-group field-group--wide">
              <span>Campagna</span>
              <input v-model.trim="campaignFilter" type="search" placeholder="cerca per nome campagna" />
            </label>
          </div>

          <div class="toolbar filters-card__toolbar">
            <button class="btn primary staff-button-neutral" @click="refresh" :disabled="loading || loadingAll">
              {{ refreshButtonLabel }}
            </button>
            <button
              v-if="!hasDateQueryFilter"
              class="btn ghost"
              @click="loadAllEvents"
              :disabled="loading || loadingAll || !hasMore"
            >
              {{ loadingAll ? 'Caricamento completo…' : 'Carica tutto il dataset' }}
            </button>
            <button class="btn ghost" @click="exportCsv" :disabled="loading || loadingAll || !filteredRows.length">
              Esporta CSV filtrato
            </button>
            <button class="btn ghost" @click="clearFilters" :disabled="loading || loadingAll">
              Pulisci filtri
            </button>
          </div>

          <div class="filters-note">
            <span>{{ datasetCoverageText }}</span>
            <span v-if="hasDateQueryFilter">
              Con un filtro data attivo, <strong>{{ refreshActionLabel }}</strong> scarica automaticamente tutto il periodo.
            </span>
            <span v-else-if="hasMore">Per un CSV completo conviene prima usare <strong>Carica tutto il dataset</strong>.</span>
          </div>
        </section>

        <section v-if="error" class="card-surface">
          <p class="error-state">{{ error }}</p>
        </section>

        <section v-else-if="loading && !events.length" class="card-surface">
          <p class="muted">Caricamento eventi in corso…</p>
        </section>

        <template v-else>
          <section class="card-surface">
            <div class="section-heading">
              <div>
                <h2>Panoramica KPI</h2>
                <p>Numeri chiave del dataset filtrato.</p>
              </div>
            </div>

            <div class="kpi-grid">
              <article v-for="card in summaryCards" :key="card.label" class="kpi-card">
                <span class="kpi-card__label">{{ card.label }}</span>
                <strong class="kpi-card__value">{{ card.value }}</strong>
                <small class="kpi-card__meta">{{ card.meta }}</small>
              </article>
            </div>
          </section>

          <section class="analysis-grid">
            <article class="card-surface chart-card">
              <div class="section-heading">
                <div>
                  <h2>Trend {{ trendGranularityLabel }}</h2>
                  <p>
                    {{ trendGranularityHint }}
                  </p>
                </div>
              </div>

              <div v-if="trendChart.points.length" class="trend-chart">
                <svg viewBox="0 0 520 180" preserveAspectRatio="none" aria-hidden="true">
                  <line
                    v-for="line in trendChart.gridLines"
                    :key="`grid-${line.y}`"
                    x1="16"
                    :y1="line.y"
                    x2="504"
                    :y2="line.y"
                    class="trend-chart__grid"
                  />
                  <path :d="trendChart.areaPath" class="trend-chart__area" />
                  <polyline :points="trendChart.polyline" class="trend-chart__line" />
                  <circle
                    v-for="point in trendChart.points"
                    :key="point.key"
                    :cx="point.x"
                    :cy="point.y"
                    r="3.5"
                    class="trend-chart__point"
                  />
                </svg>
                <div class="trend-chart__footer">
                  <span>{{ trendChart.firstLabel }}</span>
                  <span>{{ trendChart.midLabel }}</span>
                  <span>{{ trendChart.lastLabel }}</span>
                </div>
              </div>
              <p v-else class="muted">Nessun trend disponibile con i filtri correnti.</p>
            </article>

            <article class="card-surface chart-card">
              <div class="section-heading">
                <div>
                  <h2>Click per source</h2>
                  <p>Distribuzione delle sorgenti nel range filtrato.</p>
                </div>
              </div>

              <div v-if="sourceBars.length" class="bar-chart-list">
                <div v-for="row in sourceBars" :key="`source-${row.key}`" class="bar-chart-item">
                  <div class="bar-chart-item__head">
                    <div>
                      <strong>{{ row.label }}</strong>
                      <small>{{ formatPercent(row.share, 1) }} del totale</small>
                    </div>
                    <span>{{ formatNumber(row.count) }}</span>
                  </div>
                  <div class="bar-track">
                    <span class="bar-fill bar-fill--source" :style="{ width: `${ratioPercent(row.count, maxSourceCount)}%` }" />
                  </div>
                </div>
              </div>
              <p v-else class="muted">Nessuna sorgente disponibile.</p>
            </article>

            <article class="card-surface chart-card">
              <div class="section-heading">
                <div>
                  <h2>Distribuzione oraria</h2>
                  <p>In quali fasce orarie arrivano più click.</p>
                </div>
              </div>

              <div v-if="hourlySummary.length" class="hourly-chart">
                <div v-for="slot in hourlySummary" :key="slot.hour" class="hourly-chart__item">
                  <span class="hourly-chart__value">{{ slot.count }}</span>
                  <span class="hourly-chart__bar-wrap">
                    <span class="hourly-chart__bar" :style="{ height: `${ratioPercent(slot.count, maxHourlyCount)}%` }" />
                  </span>
                  <small>{{ slot.label }}</small>
                </div>
              </div>
              <p v-else class="muted">Nessun dato orario disponibile.</p>
            </article>
          </section>

          <section class="card-surface">
            <div class="section-heading">
              <div>
                <h2>Insight rapidi</h2>
                <p>Lettura sintetica dei segnali più utili.</p>
              </div>
            </div>

            <div v-if="analysisHighlights.length" class="insights-grid">
              <article v-for="item in analysisHighlights" :key="item.label" class="insight-card">
                <span class="insight-card__label">{{ item.label }}</span>
                <strong class="insight-card__value">{{ item.value }}</strong>
                <p class="insight-card__meta">{{ item.meta }}</p>
              </article>
            </div>
            <p v-else class="muted">Ancora nessun insight disponibile.</p>
          </section>

          <section class="card-surface">
            <div class="section-heading">
              <div>
                <h2>Sorgenti e campagne</h2>
                <p>Le combinazioni più presenti nel dataset filtrato.</p>
              </div>
            </div>

            <div v-if="comboSummary.length" class="leaderboard">
              <div v-for="row in comboSummary.slice(0, 6)" :key="`combo-${row.key}`" class="leaderboard__row">
                <div class="leaderboard__label">
                  <strong>{{ row.label }}</strong>
                  <small>{{ row.meta }}</small>
                </div>
                <span class="leaderboard__value">{{ formatNumber(row.count) }}</span>
              </div>
            </div>
            <p v-else class="muted">Nessuna combinazione disponibile.</p>
          </section>

          <section class="card-surface">
            <div class="section-heading">
              <div>
                <h2>Eventi clic</h2>
                <p>Elenco dettagliato degli eventi nel range filtrato.</p>
              </div>
              <span class="section-note">{{ tableSummaryText }}</span>
            </div>

            <p v-if="!filteredRows.length" class="muted">Nessun evento per i filtri attivi.</p>

            <div v-else class="table-wrap">
              <table class="events-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Source</th>
                    <th>Context</th>
                    <th>Utente</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in visibleRows" :key="row.id">
                    <td>{{ row.clickedAtLabel }}</td>
                    <td>
                      <span class="pill">{{ formatSourceLabel(row.source) }}</span>
                    </td>
                    <td class="mono">{{ row.context }}</td>
                    <td>
                      <div class="table-user">
                        <strong>{{ getUserDisplayLabel(row.userId) }}</strong>
                        <small>{{ row.userId || 'Anonimo' }}</small>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="toolbar toolbar--inline">
              <button class="btn ghost" @click="loadMore" :disabled="!canLoadMoreRows || loading || loadingAll">
                {{ loadMoreLabel }}
              </button>
              <span class="muted" v-if="filteredRows.length">
                Visibili {{ formatNumber(visibleRows.length) }} di {{ formatNumber(filteredRows.length) }} righe filtrate
              </span>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { collection, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import SidebarComponent from '../SidebarComponent.vue';
import {
  getTrackExternalLinkFunctionUrl,
  getTrackExternalYamamotoAppUrl,
  getTrackExternalYamamotoShareUrl,
} from '@/lib/env';

const YAMAMOTO_TARGET_URL = 'https://www.yamamotonutrition.com/it_it/yamamoto-x-fusco-fit';
const PROPOSED_WHATSAPP_SHORTLINK = 'https://www.metodofusco.it/whatsapp/yamamoto';
const PAGE_SIZE = 250;
const INITIAL_VISIBLE_ROWS = 80;

const numberFormatter = new Intl.NumberFormat('it-IT');

const toDateFromFirestore = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  return null;
};

const normalizeString = (value) => String(value || '').trim();

const pad = (value) => String(value).padStart(2, '0');

const buildDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const buildRefererDomain = (referer) => {
  if (!referer) return '(senza referer)';
  try {
    return new URL(referer).hostname;
  } catch (error) {
    return normalizeString(referer).slice(0, 64) || '(referer non valido)';
  }
};

const parseDateInput = (value) => {
  const [year, month, day] = String(value || '').split('-').map((segment) => Number(segment));
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDateRangeBounds = (value, endOfDay = false) => {
  const parsed = parseDateInput(value);
  if (!parsed) return null;
  if (endOfDay) {
    parsed.setHours(23, 59, 59, 999);
  } else {
    parsed.setHours(0, 0, 0, 0);
  }
  return parsed.getTime();
};

const chunkArray = (input = [], size = 10) => {
  const result = [];
  for (let index = 0; index < input.length; index += size) {
    result.push(input.slice(index, index + size));
  }
  return result;
};

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const formatPercent = (value, digits = 0) => {
  const numeric = Number.isFinite(Number(value)) ? Number(value) * 100 : 0;
  return `${numeric.toLocaleString('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
};

const formatSourceLabel = (value) => {
  const normalized = normalizeString(value) || 'unknown';
  if (normalized === 'whatsapp_email') return 'WhatsApp / Email';
  if (normalized === 'app') return 'App';
  if (normalized === 'unknown') return 'Sconosciuta';
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDateLabel = (value) => {
  const parsed = parseDateInput(value);
  if (!parsed) return value || '—';
  return parsed.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
};

const ratioPercent = (value, max) => {
  if (!Number(value || 0)) return 0;
  const safeMax = Number(max || 0);
  if (!safeMax) return 0;
  return Math.max(6, Math.round((Number(value || 0) / safeMax) * 100));
};

const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const buildDateQueryKey = (fromValue = '', toValue = '') => `${fromValue || ''}|${toValue || ''}`;

const buildSeriesChart = (series = []) => {
  if (!series.length) {
    return {
      points: [],
      polyline: '',
      areaPath: '',
      gridLines: [],
      firstLabel: '',
      midLabel: '',
      lastLabel: '',
    };
  }

  const width = 520;
  const height = 180;
  const padding = 16;
  const innerWidth = width - (padding * 2);
  const innerHeight = height - (padding * 2);
  const maxValue = Math.max(...series.map((item) => item.count), 1);

  const points = series.map((item, index) => {
    const ratio = series.length === 1 ? 0.5 : index / (series.length - 1);
    const x = padding + (ratio * innerWidth);
    const y = padding + innerHeight - ((item.count / maxValue) * innerHeight);
    return {
      ...item,
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
    };
  });

  const polyline = points.map((point) => `${point.x},${point.y}`).join(' ');
  const areaPath = [
    `M ${points[0].x} ${height - padding}`,
    ...points.map((point) => `L ${point.x} ${point.y}`),
    `L ${points[points.length - 1].x} ${height - padding}`,
    'Z',
  ].join(' ');

  const gridLines = Array.from({ length: 4 }, (_, index) => {
    const ratio = index / 3;
    return {
      y: Number((padding + (ratio * innerHeight)).toFixed(2)),
    };
  });

  const midIndex = Math.floor(points.length / 2);

  return {
    points,
    polyline,
    areaPath,
    gridLines,
    firstLabel: points[0].label,
    midLabel: points[midIndex]?.label || points[0].label,
    lastLabel: points[points.length - 1].label,
  };
};

export default {
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const events = ref([]);
    const usersById = ref({});
    const loading = ref(false);
    const loadingAll = ref(false);
    const loadingError = ref('');
    const hasMore = ref(false);
    const lastCursor = ref(null);
    const fromDate = ref('');
    const toDate = ref('');
    const sourceFilter = ref('');
    const campaignFilter = ref('');
    const copyToast = ref('');
    const shareLinksOpen = ref(false);
    const visibleRowsLimit = ref(INITIAL_VISIBLE_ROWS);
    const loadedDateQueryKey = ref('');

    let copyToastTimer = null;

    const trackExternalLinkUrl = getTrackExternalLinkFunctionUrl();
    const trackExternalYamamotoAppUrl = getTrackExternalYamamotoAppUrl();
    const trackExternalYamamotoShareUrl = getTrackExternalYamamotoShareUrl();

    const hasTrackingConfig = Boolean(trackExternalLinkUrl || trackExternalYamamotoAppUrl || trackExternalYamamotoShareUrl);

    const trackedLinks = computed(() => ([
      {
        key: 'app',
        label: 'App / gestione interna',
        description: 'Uso interno nella dashboard e nella pagina alimentazione.',
        url: trackExternalYamamotoAppUrl || YAMAMOTO_TARGET_URL,
      },
      {
        key: 'whatsapp-email',
        label: 'WhatsApp / email',
        description: 'Link da usare fuori dall’app, con tracking dedicato.',
        url: trackExternalYamamotoShareUrl || YAMAMOTO_TARGET_URL,
        maskedUrl: PROPOSED_WHATSAPP_SHORTLINK,
        maskedHint: 'Richiede una regola di redirect sul dominio root verso l’endpoint tracking.',
      },
    ]));

    const whatsappRedirectTarget = computed(() => {
      const whatsappEntry = trackedLinks.value.find((entry) => entry.key === 'whatsapp-email');
      return whatsappEntry?.url || trackExternalYamamotoShareUrl || YAMAMOTO_TARGET_URL;
    });

    const parseEvents = (docs = []) => docs
      .map((docSnap) => {
        const payload = docSnap.data() || {};
        const clickedAt = toDateFromFirestore(payload.clickedAt);
        const source = normalizeString(payload.source) || 'unknown';
        const userId = normalizeString(payload.userId) || null;
        return {
          id: docSnap.id,
          clickedAt,
          clickedAtLabel: clickedAt ? clickedAt.toLocaleString('it-IT') : '—',
          target: normalizeString(payload.target),
          source,
          campaign: normalizeString(payload.campaign) || 'default',
          context: normalizeString(payload.context) || 'link',
          userId,
          referer: normalizeString(payload.referer),
          referrerDomain: buildRefererDomain(payload.referer),
          userAgent: normalizeString(payload.userAgent),
          ip: normalizeString(payload.ip),
          dayKey: clickedAt ? buildDateKey(clickedAt) : '',
          hour: clickedAt ? clickedAt.getHours() : null,
          authenticated: Boolean(userId),
        };
      })
      .filter((item) => item.target === YAMAMOTO_TARGET_URL)
      .sort((first, second) => {
        if (!first.clickedAt && !second.clickedAt) return 0;
        if (!first.clickedAt) return 1;
        if (!second.clickedAt) return -1;
        return second.clickedAt.getTime() - first.clickedAt.getTime();
      });

    const isIndexUnavailableError = (error) => {
      const message = normalizeString(error?.message);
      return error?.code === 'failed-precondition' && message.includes('requires an index');
    };

    const fetchLinkEvents = async ({
      useOrderedQuery = true,
      useCursor = false,
      fromDateValue = '',
      toDateValue = '',
    } = {}) => {
      const constraints = [
        where('target', '==', YAMAMOTO_TARGET_URL),
      ];

      const fromDateParsed = parseDateInput(fromDateValue);
      const toDateParsed = parseDateInput(toDateValue);
      if (fromDateParsed) {
        fromDateParsed.setHours(0, 0, 0, 0);
        constraints.push(where('clickedAt', '>=', fromDateParsed));
      }
      if (toDateParsed) {
        toDateParsed.setHours(23, 59, 59, 999);
        constraints.push(where('clickedAt', '<=', toDateParsed));
      }

      if (useOrderedQuery) constraints.push(orderBy('clickedAt', 'desc'));
      constraints.push(limit(PAGE_SIZE));
      if (useOrderedQuery && useCursor && lastCursor.value) constraints.push(startAfter(lastCursor.value));

      const eventsQuery = query(collection(db, 'linkClicks'), ...constraints);
      return getDocs(eventsQuery);
    };

    const hydrateUsers = async (rows = []) => {
      const ids = [...new Set(rows.map((row) => row.userId).filter(Boolean))];
      const missing = ids.filter((id) => !usersById.value[id]);
      if (!missing.length) return;

      for (const batch of chunkArray(missing, 10)) {
        const snapshots = await Promise.all(batch.map((id) => getDoc(doc(db, 'users', id))));
        const updates = {};

        snapshots.forEach((snapshot, index) => {
          const id = batch[index];
          if (!snapshot || !snapshot.exists()) {
            updates[id] = { notFound: true };
            return;
          }

          const data = snapshot.data() || {};
          updates[id] = {
            email: normalizeString(data.email),
            name: normalizeString(data.name),
            surname: normalizeString(data.surname),
          };
        });

        usersById.value = { ...usersById.value, ...updates };
      }
    };

    const hasDateQueryFilter = computed(() => Boolean(fromDate.value || toDate.value));

    const isLoadedDateRangeAligned = computed(() => (
      !hasDateQueryFilter.value || loadedDateQueryKey.value === buildDateQueryKey(fromDate.value, toDate.value)
    ));

    const loadEventsPage = async (
      append = false,
      {
        fromDateValue = '',
        toDateValue = '',
        keepLoadingFlag = false,
      } = {},
    ) => {
      if (!keepLoadingFlag) loading.value = true;
      loadingError.value = '';

      try {
        const snapshot = await fetchLinkEvents({
          useOrderedQuery: true,
          useCursor: append,
          fromDateValue,
          toDateValue,
        });
        const nextPage = parseEvents(snapshot.docs);

        events.value = append ? [...events.value, ...nextPage] : nextPage;
        hasMore.value = snapshot.size === PAGE_SIZE;
        lastCursor.value = snapshot.docs[snapshot.docs.length - 1] || null;

        await hydrateUsers(nextPage);
        loadedDateQueryKey.value = buildDateQueryKey(fromDateValue, toDateValue);
      } catch (error) {
        if (!append && !fromDateValue && !toDateValue && isIndexUnavailableError(error)) {
          try {
            const snapshot = await fetchLinkEvents({
              useOrderedQuery: false,
              useCursor: false,
            });
            const nextPage = parseEvents(snapshot.docs);

            events.value = nextPage;
            hasMore.value = false;
            lastCursor.value = null;
            await hydrateUsers(nextPage);
            loadedDateQueryKey.value = buildDateQueryKey('', '');
            loadingError.value = 'Indice Firestore in costruzione: i dati sono mostrati senza ordinamento lato DB.';
            return;
          } catch (fallbackError) {
            console.error('Errore fallback caricamento linkClicks:', fallbackError);
          }
        }

        loadingError.value = fromDateValue || toDateValue
          ? 'Errore nel caricamento completo del periodo selezionato.'
          : 'Errore nel caricamento degli eventi. Riprova più tardi.';
        console.error('Errore caricamento linkClicks:', error);
      } finally {
        if (!keepLoadingFlag) loading.value = false;
      }
    };

    const refresh = async () => {
      events.value = [];
      hasMore.value = false;
      lastCursor.value = null;
      visibleRowsLimit.value = INITIAL_VISIBLE_ROWS;
      loadedDateQueryKey.value = '';

      if (hasDateQueryFilter.value) {
        await loadAllEvents({
          fromDateValue: fromDate.value,
          toDateValue: toDate.value,
          isRangeRefresh: true,
        });
        return;
      }

      await loadEventsPage(false, {
        fromDateValue: '',
        toDateValue: '',
      });
    };

    const loadAllEvents = async ({
      fromDateValue = '',
      toDateValue = '',
      isRangeRefresh = false,
    } = {}) => {
      if (loading.value || loadingAll.value || (!isRangeRefresh && !hasMore.value)) return;
      loadingAll.value = true;
      loading.value = true;
      loadingError.value = '';

      try {
        const effectiveFromDate = fromDateValue || '';
        const effectiveToDate = toDateValue || '';

        events.value = [];
        hasMore.value = false;
        lastCursor.value = null;
        loadedDateQueryKey.value = '';

        let append = false;
        let safety = 0;
        while (safety < 100) {
          await loadEventsPage(append, {
            fromDateValue: effectiveFromDate,
            toDateValue: effectiveToDate,
            keepLoadingFlag: true,
          });
          safety += 1;
          if (!hasMore.value) break;
          append = true;
        }

        hasMore.value = false;
        lastCursor.value = null;
      } finally {
        loadingAll.value = false;
        loading.value = false;
      }
    };

    const clearFilters = () => {
      fromDate.value = '';
      toDate.value = '';
      sourceFilter.value = '';
      campaignFilter.value = '';
    };

    const filteredRows = computed(() => {
      const fromMs = getDateRangeBounds(fromDate.value, false);
      const toMs = getDateRangeBounds(toDate.value, true);
      const campaignNeedle = normalizeString(campaignFilter.value).toLowerCase();

      return events.value.filter((event) => {
        const eventTs = event.clickedAt ? event.clickedAt.getTime() : null;
        if (sourceFilter.value && event.source !== sourceFilter.value) return false;
        if (campaignNeedle && !event.campaign.toLowerCase().includes(campaignNeedle)) return false;
        if (fromMs && (!eventTs || eventTs < fromMs)) return false;
        if (toMs && (!eventTs || eventTs > toMs)) return false;
        return true;
      });
    });

    const visibleRows = computed(() => filteredRows.value.slice(0, visibleRowsLimit.value));

    const canLoadMoreRows = computed(() => (
      visibleRowsLimit.value < filteredRows.value.length || hasMore.value
    ));

    const loadMoreLabel = computed(() => {
      if (visibleRowsLimit.value < filteredRows.value.length) return 'Mostra altre righe';
      if (hasMore.value) return 'Carica altri eventi da Firestore';
      return 'Nessun altro evento';
    });

    const loadMore = async () => {
      if (visibleRowsLimit.value < filteredRows.value.length) {
        visibleRowsLimit.value += INITIAL_VISIBLE_ROWS;
        return;
      }
      if (!hasMore.value || loading.value || loadingAll.value) return;
      await loadEventsPage(true, {
        fromDateValue: '',
        toDateValue: '',
      });
      visibleRowsLimit.value += INITIAL_VISIBLE_ROWS;
    };

    const sourceOptions = computed(() => {
      const options = new Set();
      events.value.forEach((event) => {
        if (event.source) options.add(event.source);
      });
      return Array.from(options).sort();
    });

    const authenticatedClicks = computed(() => filteredRows.value.filter((event) => event.authenticated).length);
    const anonymousClicks = computed(() => filteredRows.value.length - authenticatedClicks.value);

    const uniqueUserCount = computed(() => {
      const set = new Set();
      filteredRows.value.forEach((event) => {
        if (event.userId) set.add(event.userId);
      });
      return set.size;
    });

    const sourceSummary = computed(() => {
      const totals = Object.create(null);

      filteredRows.value.forEach((event) => {
        const key = event.source || 'unknown';
        if (!totals[key]) {
          totals[key] = { key, label: formatSourceLabel(key), count: 0 };
        }
        totals[key].count += 1;
      });

      const totalCount = filteredRows.value.length || 1;
      return Object.values(totals)
        .map((row) => ({
          ...row,
          share: row.count / totalCount,
        }))
        .sort((first, second) => second.count - first.count);
    });

    const campaignSummary = computed(() => {
      const totals = Object.create(null);

      filteredRows.value.forEach((event) => {
        const key = event.campaign || 'default';
        if (!totals[key]) {
          totals[key] = {
            key,
            label: key,
            count: 0,
            uniqueUsersSet: new Set(),
          };
        }

        totals[key].count += 1;
        if (event.userId) totals[key].uniqueUsersSet.add(event.userId);
      });

      return Object.values(totals)
        .map((row) => ({
          key: row.key,
          label: row.label,
          count: row.count,
          uniqueUsers: row.uniqueUsersSet.size,
        }))
        .sort((first, second) => second.count - first.count);
    });

    const comboSummary = computed(() => {
      const totals = Object.create(null);

      filteredRows.value.forEach((event) => {
        const key = `${event.source || 'unknown'}__${event.campaign || 'default'}`;
        if (!totals[key]) {
          totals[key] = {
            key,
            label: `${formatSourceLabel(event.source)} / ${event.campaign || 'default'}`,
            meta: event.context || 'link',
            count: 0,
          };
        }
        totals[key].count += 1;
      });

      return Object.values(totals).sort((first, second) => second.count - first.count);
    });

    const dailyTrend = computed(() => {
      if (!filteredRows.value.length) return [];

      const totals = Object.create(null);
      filteredRows.value.forEach((event) => {
        if (!event.dayKey) return;
        totals[event.dayKey] = (totals[event.dayKey] || 0) + 1;
      });

      const keys = Object.keys(totals).sort();
      const startDate = parseDateInput(fromDate.value) || parseDateInput(keys[0]);
      const endDate = parseDateInput(toDate.value) || parseDateInput(keys[keys.length - 1]);
      if (!startDate || !endDate) {
        return keys.map((key) => ({
          key,
          label: formatDateLabel(key),
          count: totals[key],
        }));
      }

      const rows = [];
      const cursor = new Date(startDate);
      cursor.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      while (cursor.getTime() <= endDate.getTime()) {
        const key = buildDateKey(cursor);
        rows.push({
          key,
          label: formatDateLabel(key),
          count: totals[key] || 0,
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      return rows;
    });

    const trendSeries = computed(() => {
      if (dailyTrend.value.length <= 42) return dailyTrend.value;

      const grouped = [];
      for (let index = 0; index < dailyTrend.value.length; index += 7) {
        const chunk = dailyTrend.value.slice(index, index + 7);
        grouped.push({
          key: `${chunk[0].key}-${chunk[chunk.length - 1].key}`,
          label: `${chunk[0].label} - ${chunk[chunk.length - 1].label}`,
          count: chunk.reduce((total, item) => total + item.count, 0),
        });
      }
      return grouped;
    });

    const trendGranularityLabel = computed(() => (
      dailyTrend.value.length > 42 ? 'settimanale' : 'giornaliero'
    ));

    const trendGranularityHint = computed(() => (
      dailyTrend.value.length > 42
        ? 'Serie aggregata a blocchi da 7 giorni per mantenere il grafico leggibile.'
        : 'Andamento dei click giorno per giorno nel periodo filtrato.'
    ));

    const trendChart = computed(() => buildSeriesChart(trendSeries.value));

    const hourlySummary = computed(() => {
      if (!filteredRows.value.length) return [];

      const counts = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        label: pad(hour),
        count: 0,
      }));

      filteredRows.value.forEach((event) => {
        if (Number.isInteger(event.hour) && counts[event.hour]) {
          counts[event.hour].count += 1;
        }
      });

      return counts;
    });

    const last7DaysClicks = computed(() => {
      const floor = new Date();
      floor.setHours(0, 0, 0, 0);
      floor.setDate(floor.getDate() - 6);
      return filteredRows.value.filter((event) => event.clickedAt && event.clickedAt >= floor).length;
    });

    const peakDay = computed(() => {
      if (!dailyTrend.value.length) return null;
      return dailyTrend.value.reduce((best, current) => (
        !best || current.count > best.count ? current : best
      ), null);
    });

    const peakHour = computed(() => {
      if (!hourlySummary.value.length) return null;
      return hourlySummary.value.reduce((best, current) => (
        !best || current.count > best.count ? current : best
      ), null);
    });

    const authRate = computed(() => (
      filteredRows.value.length ? authenticatedClicks.value / filteredRows.value.length : 0
    ));

    const summaryCards = computed(() => ([
      {
        label: 'Click totali',
        value: formatNumber(filteredRows.value.length),
        meta: 'Eventi nel report filtrato.',
      },
      {
        label: 'Utenti unici',
        value: formatNumber(uniqueUserCount.value),
        meta: 'Solo utenti autenticati riconosciuti.',
      },
      {
        label: 'Tasso autenticati',
        value: formatPercent(authRate.value, 1),
        meta: `${formatNumber(authenticatedClicks.value)} autenticati su ${formatNumber(filteredRows.value.length)} click.`,
      },
      {
        label: 'Ultimi 7 giorni',
        value: formatNumber(last7DaysClicks.value),
        meta: 'Velocizza la lettura del trend recente.',
      },
      {
        label: 'Giorno picco',
        value: peakDay.value ? formatNumber(peakDay.value.count) : '0',
        meta: peakDay.value ? peakDay.value.label : 'Nessun picco rilevato.',
      },
    ]));

    const sourceBars = computed(() => sourceSummary.value.slice(0, 6));
    const maxSourceCount = computed(() => Math.max(...sourceBars.value.map((row) => row.count), 0));

    const maxHourlyCount = computed(() => Math.max(...hourlySummary.value.map((row) => row.count), 0));

    const analysisHighlights = computed(() => {
      const items = [];
      const topSource = sourceSummary.value[0];

      if (topSource) {
        items.push({
          label: 'Sorgente dominante',
          value: topSource.label,
          meta: `${formatNumber(topSource.count)} click, pari al ${formatPercent(topSource.share, 1)} del totale.`,
        });
      }

      if (peakDay.value) {
        items.push({
          label: 'Giorno più forte',
          value: peakDay.value.label,
          meta: `${formatNumber(peakDay.value.count)} click concentrati in questa giornata.`,
        });
      }

      if (peakHour.value) {
        items.push({
          label: 'Fascia oraria migliore',
          value: `${peakHour.value.label}:00`,
          meta: `${formatNumber(peakHour.value.count)} click registrati in quest’ora.`,
        });
      }

      return items.slice(0, 4);
    });

    const loadedInfo = computed(() => {
      if (loading.value && !events.value.length) return 'Caricamento eventi…';
      if (!events.value.length) return 'Nessun evento caricato.';

      const latest = events.value[0];
      const earliest = events.value[events.value.length - 1];
      const base = hasMore.value
        ? `Caricati ${formatNumber(events.value.length)} eventi, ma il dataset non è ancora completo.`
        : `Caricati ${formatNumber(events.value.length)} eventi.`;

      if (!latest?.clickedAtLabel || !earliest?.clickedAtLabel) return base;
      return `${base} Finestra disponibile: dal ${earliest.clickedAtLabel} al ${latest.clickedAtLabel}.`;
    });

    const datasetCoverageText = computed(() => (
      hasDateQueryFilter.value && !isLoadedDateRangeAligned.value
        ? `Hai cambiato il periodo: premi "${refreshActionLabel.value}" per scaricare tutti gli eventi del range selezionato.`
        : hasDateQueryFilter.value
          ? `Il report corrente include l'intero dataset del periodo selezionato.`
          : hasMore.value
            ? `Al momento stai leggendo solo gli ultimi ${formatNumber(events.value.length)} eventi caricati.`
            : `Il dataset caricato copre ${formatNumber(events.value.length)} eventi ed è completo per la query attuale.`
    ));

    const refreshActionLabel = computed(() => (
      hasDateQueryFilter.value ? 'Carica intero periodo' : 'Ricarica ultimi eventi'
    ));

    const refreshButtonLabel = computed(() => {
      if (loading.value && hasDateQueryFilter.value) return 'Caricamento periodo…';
      if (loading.value) return 'Aggiornamento…';
      return refreshActionLabel.value;
    });

    const filterSummaryText = computed(() => {
      const chunks = [];
      if (fromDate.value) chunks.push(`dal ${formatDateLabel(fromDate.value)}`);
      if (toDate.value) chunks.push(`al ${formatDateLabel(toDate.value)}`);
      if (sourceFilter.value) chunks.push(`source ${formatSourceLabel(sourceFilter.value)}`);
      if (campaignFilter.value) chunks.push(`campagna contiene "${campaignFilter.value}"`);
      return chunks.length ? chunks.join(' · ') : 'Nessun filtro aggiuntivo attivo';
    });

    const tableSummaryText = computed(() => {
      if (!filteredRows.value.length) return 'Nessun evento nel filtro.';
      return `Tabella dettagliata su ${formatNumber(filteredRows.value.length)} eventi filtrati.`;
    });

    const getUserDisplayLabel = (userId) => {
      if (!userId) return 'Anonimo';
      const profile = usersById.value[userId];
      if (!profile || profile.notFound) return userId;

      const fullName = [profile.name, profile.surname].filter(Boolean).join(' ').trim();
      if (fullName && profile.email) return `${fullName} (${profile.email})`;
      if (fullName) return fullName;
      if (profile.email) return profile.email;
      return userId;
    };

    const showCopyFeedback = (message, duration = 1700) => {
      copyToast.value = message;
      if (copyToastTimer) clearTimeout(copyToastTimer);
      copyToastTimer = setTimeout(() => {
        copyToast.value = '';
      }, duration);
    };

    const copyToClipboard = async (text, label) => {
      if (!text) return;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        showCopyFeedback(`${label} copiato`);
      } catch (error) {
        console.error('Errore copia link', error);
        showCopyFeedback('Impossibile copiare automaticamente. Copia manualmente.', 2500);
      }
    };

    const downloadCsv = (content, filename) => {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const buildCsvFilename = () => {
      const from = fromDate.value || 'inizio';
      const to = toDate.value || 'oggi';
      const source = sourceFilter.value || 'tutte';
      return `link_clicks_${from}_${to}_${source}.csv`;
    };

    const exportCsv = () => {
      if (!filteredRows.value.length) return;

      const headers = [
        'id',
        'clicked_at',
        'source',
        'campaign',
        'context',
        'user_id',
        'user_label',
        'authenticated',
        'referer_domain',
        'referer',
        'target',
        'user_agent',
        'ip',
      ];

      const lines = [
        headers.join(';'),
        ...filteredRows.value.map((row) => ([
          row.id,
          row.clickedAtLabel,
          row.source,
          row.campaign,
          row.context,
          row.userId || '',
          getUserDisplayLabel(row.userId),
          row.authenticated ? 'yes' : 'no',
          row.referrerDomain,
          row.referer,
          row.target,
          row.userAgent,
          row.ip,
        ].map(escapeCsvValue).join(';'))),
      ];

      downloadCsv(lines.join('\n'), buildCsvFilename());
    };

    watch([sourceFilter, campaignFilter, fromDate, toDate], () => {
      visibleRowsLimit.value = INITIAL_VISIBLE_ROWS;
    });

    onMounted(() => {
      refresh();
    });

    onBeforeUnmount(() => {
      if (copyToastTimer) clearTimeout(copyToastTimer);
    });

    return {
      events,
      error: loadingError,
      loading,
      loadingAll,
      hasMore,
      hasDateQueryFilter,
      hasTrackingConfig,
      fromDate,
      toDate,
      sourceFilter,
      campaignFilter,
      shareLinksOpen,
      sourceOptions,
      trackedLinks,
      whatsappRedirectTarget,
      copyToast,
      refresh,
      loadAllEvents,
      exportCsv,
      clearFilters,
      loadMore,
      loadMoreLabel,
      canLoadMoreRows,
      filteredRows,
      visibleRows,
      visibleRowsLimit,
      authenticatedClicks,
      anonymousClicks,
      uniqueUserCount,
      sourceSummary,
      campaignSummary,
      comboSummary,
      summaryCards,
      sourceBars,
      maxSourceCount,
      hourlySummary,
      maxHourlyCount,
      trendChart,
      trendGranularityLabel,
      trendGranularityHint,
      analysisHighlights,
      loadedInfo,
      datasetCoverageText,
      filterSummaryText,
      refreshActionLabel,
      refreshButtonLabel,
      tableSummaryText,
      getUserDisplayLabel,
      copyToClipboard,
      formatNumber,
      formatPercent,
      formatSourceLabel,
      ratioPercent,
    };
  },
};
</script>

<style scoped>
.link-clicks-page {
  --ink: #0f172a;
  --muted: #475569;
  --line: #dbe4ee;
  --line-strong: #c6d4e1;
  --surface: #ffffff;
  --surface-soft: #f8fafc;
  --accent: #0f766e;
  --accent-soft: #ccfbf1;
  --accent-strong: #0d9488;
  --campaign: #f59e0b;
  --campaign-soft: #fef3c7;
  --warning: #b45309;
  --warning-soft: #fffbeb;
  --danger: #991b1b;
  color: var(--ink);
}

.link-clicks-page h1,
.link-clicks-page h2,
.link-clicks-page h3,
.link-clicks-page strong,
.link-clicks-page span,
.link-clicks-page td,
.link-clicks-page th,
.link-clicks-page p,
.link-clicks-page small {
  color: inherit;
}

.analytics-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 20px;
  margin-bottom: 18px;
  padding: 24px;
  border: 1px solid #d7ebe8;
  border-radius: 22px;
  background:
    radial-gradient(circle at top right, rgba(15, 148, 136, 0.17), transparent 34%),
    linear-gradient(135deg, #f7fbfb 0%, #ffffff 55%, #eefbf8 100%);
}

.eyebrow {
  margin: 0 0 8px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
}

.subtitle {
  max-width: 700px;
  margin: 8px 0 0;
  color: var(--muted);
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 18px;
}

.hero-meta__text {
  color: var(--muted);
  font-size: 0.95rem;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.status-chip--ok {
  background: var(--accent-soft);
  color: #115e59;
}

.status-chip--warn {
  background: var(--warning-soft);
  color: var(--warning);
}

.hero-highlight {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 18px;
  border: 1px solid rgba(15, 118, 110, 0.16);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.06);
}

.hero-highlight__label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
}

.hero-highlight__value {
  font-size: 2.5rem;
  line-height: 1;
}

.hero-highlight__meta {
  color: var(--muted);
  font-size: 0.92rem;
}

.card-surface {
  margin-bottom: 18px;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: var(--surface);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.035);
}

.accordion {
  overflow: hidden;
}

.accordion__summary {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  background: #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}

.accordion__summary:hover {
  background: #dbe3ec;
  border-color: #b8c4d3;
}

.accordion__heading {
  flex: 1;
}

.accordion__heading--left {
  text-align: left;
}

.accordion__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
}

.accordion__icon {
  flex: none;
  color: #475569;
  font-size: 28px;
  transition: transform 0.18s ease;
}

.accordion__icon--open {
  transform: rotate(180deg);
}

.accordion__content {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h2 {
  margin: 0;
}

.section-heading p {
  margin: 6px 0 0;
  color: var(--muted);
}

.section-note {
  color: var(--muted);
  font-size: 0.9rem;
  text-align: right;
}

.share-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.share-card {
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.share-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.share-card__header h3 {
  margin: 0;
}

.share-card__header p {
  margin: 6px 0 0;
  color: var(--muted);
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
}

.tag--accent {
  background: var(--campaign-soft);
  color: #92400e;
}

.tag--neutral {
  background: #eef2ff;
  color: #4338ca;
}

.link-stack {
  display: grid;
  gap: 12px;
}

.link-block {
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface-soft);
}

.link-block--secondary {
  border-style: dashed;
}

.link-block__label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
}

.link-block__value {
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #ffffff;
  font-size: 0.86rem;
  line-height: 1.45;
  word-break: break-all;
}

.link-block__hint {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.shortlink-note,
.info-banner {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid var(--line-strong);
  background: var(--surface-soft);
  color: var(--muted);
}

.info-banner--warn {
  border-color: #f7d9a9;
  background: var(--warning-soft);
  color: #92400e;
}

.mono-inline {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier, monospace;
  font-size: 0.9rem;
}

.copy-toast {
  margin: 12px 0 0;
  color: var(--muted);
}

.filters-card {
  border-color: #d6e3ef;
  background:
    linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.filters-card__heading {
  margin-bottom: 18px;
}

.filters-card__heading h2 {
  line-height: 1.05;
}

.filters-card .toolbar {
  margin-top: 18px;
}

.filters-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(0, 1fr) minmax(0, 1.8fr);
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.field-group span {
  font-size: 0.84rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #334155;
}

.field-group--wide {
  grid-column: span 1;
}

.field-group input,
.field-group select {
  width: 100%;
  height: 56px;
  padding: 0 16px;
  box-sizing: border-box;
  border: 1px solid var(--line-strong);
  border-radius: 16px;
  background: #ffffff;
  color: var(--ink);
  font-size: 1rem;
  font-weight: 500;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.field-group input::placeholder {
  color: #94a3b8;
}

.field-group input:focus,
.field-group select:focus {
  outline: none;
  border-color: rgba(15, 118, 110, 0.55);
  box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.12);
}

.field-group input[type='date'] {
  appearance: none;
  -webkit-appearance: none;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.filters-card__toolbar {
  align-items: center;
}

.filters-card__toolbar .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 18px;
  border: 1px solid #d5dee8;
  border-radius: 14px;
  font-size: 0.96rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease, background-color 0.16s ease;
}

.filters-card__toolbar .btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.filters-card__toolbar .btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.filters-card__toolbar .btn.primary {
  border-color: #0f766e;
  background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
  color: #ffffff;
  box-shadow: 0 12px 24px rgba(15, 118, 110, 0.16);
}

.filters-card__toolbar .btn.ghost {
  background: #f8fafc;
  color: #1e293b;
}

.filters-card__toolbar .btn.ghost:hover:not(:disabled) {
  border-color: #93c5c0;
  background: #f0fdfa;
}

.toolbar--inline {
  align-items: center;
  margin-top: 14px;
}

.filters-note,
.muted {
  color: var(--muted);
}

.filters-note {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e5edf4;
  font-size: 0.94rem;
  line-height: 1.5;
}

.error-state {
  margin: 0;
  color: var(--danger);
}

.kpi-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.kpi-card,
.insight-card {
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbfe 100%);
}

.kpi-card__label,
.insight-card__label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
}

.kpi-card__value,
.insight-card__value {
  font-size: 1.8rem;
  line-height: 1.1;
}

.kpi-card__meta,
.insight-card__meta {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  line-height: 1.45;
}

.analysis-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}

.chart-card {
  margin-bottom: 0;
}

.trend-chart {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.trend-chart svg {
  width: 100%;
  height: 200px;
}

.trend-chart__grid {
  stroke: #e5edf4;
  stroke-width: 1;
}

.trend-chart__area {
  fill: rgba(13, 148, 136, 0.12);
}

.trend-chart__line {
  fill: none;
  stroke: var(--accent-strong);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.trend-chart__point {
  fill: var(--accent-strong);
}

.trend-chart__footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: var(--muted);
  font-size: 0.86rem;
}

.bar-chart-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-chart-item__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 8px;
}

.bar-chart-item__head small {
  display: block;
  margin-top: 4px;
  color: var(--muted);
}

.bar-track {
  width: 100%;
  height: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf2f7;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.bar-fill--source {
  background: linear-gradient(90deg, #14b8a6 0%, #0f766e 100%);
}

.bar-fill--campaign {
  background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%);
}

.hourly-chart {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  align-items: end;
}

.hourly-chart__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.hourly-chart__value {
  min-height: 18px;
  font-size: 0.78rem;
  color: var(--muted);
}

.hourly-chart__bar-wrap {
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: 88px;
  padding: 4px;
  border-radius: 10px;
  background: #eef4f8;
}

.hourly-chart__bar {
  width: 100%;
  min-height: 6px;
  border-radius: 8px 8px 4px 4px;
  background: linear-gradient(180deg, #36cfc9 0%, #0f766e 100%);
}

.insights-grid,
.detail-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.leaderboard {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.leaderboard__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface-soft);
}

.leaderboard__label small {
  display: block;
  margin-top: 4px;
  color: var(--muted);
}

.leaderboard__value {
  font-weight: 700;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid var(--line);
  border-radius: 16px;
}

.events-table {
  width: 100%;
  border-collapse: collapse;
}

.events-table th,
.events-table td {
  padding: 12px 14px;
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

.events-table thead th {
  background: #f4f8fb;
  font-size: 0.86rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
}

.events-table tbody tr:hover {
  background: #fbfdff;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier, monospace;
  font-size: 0.85rem;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef6f5;
  color: #115e59;
  font-size: 0.82rem;
  font-weight: 700;
}

.table-user {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table-user small {
  color: var(--muted);
  line-height: 1.4;
}

.copy-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

@media (max-width: 1080px) {
  .analytics-hero,
  .analysis-grid,
  .insights-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .filters-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hourly-chart {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .analytics-hero {
    padding: 18px;
  }

  .card-surface {
    padding: 14px;
    border-radius: 16px;
  }

  .section-heading {
    flex-direction: column;
  }

  .accordion__summary,
  .accordion__meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .section-note {
    text-align: left;
  }

  .hero-highlight__value {
    font-size: 2.1rem;
  }

  .kpi-grid,
  .share-grid {
    grid-template-columns: 1fr;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .filters-card__toolbar .btn {
    width: 100%;
  }

  .hourly-chart {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .events-table th,
  .events-table td {
    min-width: 160px;
  }
}
</style>
